import { memo, useCallback, useContext, useMemo } from "react";

import { entities } from "byzantine";
import { Alert, Button, Dialog } from "@narmi/design_system";
import { Localized, useLocalization } from "@fluent/react";
import { DateTime } from "luxon";

import { useCurrentUser } from "../../../../components/contexts/CurrentUserContext";

import styles from "./DocumentShareDialog.module.scss";
import { CheckboxContext } from "./CheckboxContext";

import type { DialogProps } from "@narmi/design_system/dist/types/Dialog";

const { useCreateShareDocuments } = entities.documents;

interface DocumentShareDialogProps
  extends Pick<DialogProps, "isOpen" | "onUserDismiss"> {}

const DocumentShareDialog = ({
  isOpen,
  onUserDismiss,
}: DocumentShareDialogProps) => {
  const { l10n } = useLocalization();
  const { createShareDocuments, loading } = useCreateShareDocuments();
  const { checkBoxState } = useContext(CheckboxContext);
  const { currentUser } = useCurrentUser();
  const { email: emailAddress = "" } = currentUser || {};

  const shareHandler = useCallback(() => {
    createShareDocuments(Object.values(checkBoxState), onUserDismiss);
  }, [createShareDocuments, checkBoxState, onUserDismiss]);

  const footerButtons = useMemo(
    () => (
      <div className={styles["button-container"]}>
        <Button
          label={l10n.getString("document-share-dialog-button-close")}
          kind="negative"
          onClick={onUserDismiss}
        />
        <Button
          label={l10n.getString("document-share-dialog-button-share")}
          isLoading={loading}
          onClick={shareHandler}
        />
      </div>
    ),
    [l10n, loading, onUserDismiss, shareHandler]
  );

  return (
    <Dialog
      isOpen={isOpen}
      onUserDismiss={onUserDismiss}
      title={l10n.getString("document-share-dialog-title")}
      footer={footerButtons}
    >
      <div className={styles["dialog-body"]}>
        <Localized
          id="document-share-dialog-instructions"
          vars={{ emailAddress }}
          elems={{ boldedText: <strong /> }}
        >
          <span className={styles["body-text"]}>
            An email will be sent to <strong>{emailAddress}</strong>.
          </span>
        </Localized>

        <div className={styles["download-section-container"]}>
          <span className={styles["download-section-primary-text"]}>
            <Localized
              id="document-share-dialog-share-overview"
              vars={{ count: Object.keys(checkBoxState).length }}
            />
          </span>

          <div className={styles["download-section-divider"]} />

          {Object.entries(checkBoxState).map(
            ([documentId, { type_label, created_at }]) => (
              <div key={documentId} className={styles["download-item"]}>
                <span className={styles["download-section-primary-text"]}>
                  {type_label}
                </span>
                <span className={styles["body-text"]}>
                  {DateTime.fromISO(created_at).toFormat("LLLL yyyy")}
                </span>
              </div>
            )
          )}
        </div>

        <Alert isActive isDismissable={false}>
          <Localized id="document-share-dialog-share-data-disclaimer" />
        </Alert>

        <span className={styles["body-text"]}>
          <Localized id="document-share-dialog-share-email-disclaimer" />
        </span>
      </div>
    </Dialog>
  );
};

export default memo(DocumentShareDialog);
